import { useMutation } from 'urql';

import {
  MenuItem,
  MenuList,
  type MenuListProps,
  RevelioTooltip,
} from '@revelio/ui';

import { showAlertDialog } from '../../../shared/components/alert-dialog';
import type { UnifiedReport } from '../hooks/use-all-reports';
import {
  DeleteReportMutation,
  RefreshReportMutation,
} from '../report-builder/report-builder-operations';
import { disabledReportReruns } from '../utils/disabledReportReruns';

type ReportMenuListAction = 'delete' | 'refresh';

export type ReportMenuListProps = Omit<MenuListProps, 'children'> & {
  report: UnifiedReport;
  viewReportLink?: string | null;
  onActionTriggered?: (action: ReportMenuListAction, reportId: string) => void;
  onActionSucceeded?: (action: ReportMenuListAction, reportId: string) => void;
};

export const MiddlewareReportMenuList = ({
  report,
  viewReportLink,
  onActionTriggered,
  onActionSucceeded,
  ...rest
}: ReportMenuListProps) => {
  const [, refreshReport] = useMutation(RefreshReportMutation);
  const [, deleteReport] = useMutation(DeleteReportMutation);
  const { reportType, id: reportId } = report;

  const reportDisabledReason = disabledReportReruns(report);

  const onRefreshReport = async () => {
    if (reportType === 'GeneratedByRevelio') {
      return showAlertDialog({
        alertType: 'actionless',
        isOpen: true,
        headerText: 'Unable to Refresh',
        bodyContent: (
          <span>
            This report was generated for you and is unable to be refreshed in
            this way. Please contact us to request an update.
          </span>
        ),
      });
    }

    onActionTriggered && onActionTriggered('refresh', reportId);
    const result = await refreshReport(
      { id: reportId },
      { additionalTypenames: ['Report'] }
    );
    if (!result.error) {
      onActionSucceeded && onActionSucceeded('refresh', reportId);
    }
  };

  const onDeleteReport = () => {
    if (reportType === 'GeneratedByRevelio') {
      return showAlertDialog({
        alertType: 'actionless',
        isOpen: true,
        headerText: 'Unable to Delete',
        bodyContent: (
          <span>
            This report was generated for you and is unable to be deleted in
            this way. Please contact us to remove it.
          </span>
        ),
      });
    }

    showAlertDialog({
      alertType: 'confirm',
      isOpen: true,
      headerText: 'Are you sure you want to delete this report?',
      bodyContent: (
        <span>
          This action is permanent and the report can not be recovered.
        </span>
      ),
      footerAcceptIsDestructive: true,
      footerAcceptText: 'Yes, Delete',
      onAccept: async () => {
        onActionTriggered && onActionTriggered('delete', reportId);
        const result = await deleteReport(
          { id: reportId },
          { additionalTypenames: ['Report'] }
        );
        if (!result.error) {
          onActionSucceeded && onActionSucceeded('delete', reportId);
        }
      },
    });
  };

  return (
    <MenuList {...rest}>
      {viewReportLink && <MenuItem href={viewReportLink}>View</MenuItem>}

      <RevelioTooltip
        label={reportDisabledReason ?? ''}
        placement="right"
        isDisabled={!reportDisabledReason}
        fullWidthTrigger
      >
        <MenuItem onClick={onRefreshReport} disabled={!!reportDisabledReason}>
          Rerun
        </MenuItem>
      </RevelioTooltip>

      <MenuItem onClick={onDeleteReport} destructive>
        Delete
      </MenuItem>
    </MenuList>
  );
};
